import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/authContext";
import { useLoading } from "../../context/loadingContext";
import { useNotification } from "../../context/notificationContext";
import { isSuccessCode } from "../../helpers/apiResponse";
import * as EventAPI from "../../api/event";
import * as ReportAPI from "../../api/report";
import { members } from "../../api/user";
import TableComponent from "../../component/form/TableComponent";
import { Column, TableButtons } from "../../types/TableComponent.types";
import { EventItem } from "../../types/api/event.type";
import { ReportItem } from "../../types/api/report.type";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBell, faEye, faNotesMedical } from "@fortawesome/free-solid-svg-icons";
import { LoadingScreenLocal } from "../other/LoadingScreen";
import {
    buildDateRange,
    findMemberReportForDay,
    resolveOwnerId,
    toEventItem,
    toReportItem,
    todayDateOnly,
} from "./reportOverview.helpers";
import "./ReportEventOverviewScreen.css";

type MemberItem = {
    id: number;
    name: string;
    surname: string;
    role: string;
};

const ROLE_KEYS = ["role", "association_role", "member_role"];
const getRole = (member: Record<string, unknown>): string => {
    const role = ROLE_KEYS.map((key) => member[key]).find((value) => value !== undefined);
    if (Array.isArray(role)) return String(role[0] ?? "");
    return String(role ?? "");
};

const ReportEventOverviewScreen: React.FC = () => {
    const params = new URLSearchParams(window.location.search);
    const eventId = Number(params.get("event_id") ?? 0);

    const { association, associationRole, user } = useAuth();
    const { loading, setLoadingStatus } = useLoading();
    const { addNotification } = useNotification();
    const nav = useNavigate();

    const associationId = String(association.id);
    const today = useMemo(() => todayDateOnly(), []);
    const isAthleteView = associationRole === "athlete" || associationRole === "parent";
    const isCoachOrPresident = associationRole === "coach" || associationRole === "president";

    const [eventData, setEventData] = useState<EventItem | null>(null);
    const [reports, setReports] = useState<ReportItem[]>([]);
    const [athletes, setAthletes] = useState<MemberItem[]>([]);

    const handleApiError = useCallback((requestId: string, errorCode: string) => {
        nav(`/500?process_id=${requestId}&error_code=${errorCode}`);
    }, [nav]);

    const sendReminderForMissingReport = useCallback((payload: { day: string; athlete_user_id?: number }) => {
        // TO IMPLEMENT
        addNotification("Reminder queued", `Reminder scheduled for ${payload.day}`, "success");
    }, [addNotification]);

    const loadData = useCallback(async () => {
        if (!eventId) {
            addNotification("Invalid event", "Missing event id", "error");
            nav("/reports");
            return;
        }

        const [eventResponse, reportResponse] = await Promise.all([
            EventAPI.detail(eventId),
            ReportAPI.list(associationId),
        ]);

        if (!isSuccessCode(eventResponse.error_code)) {
            return handleApiError(eventResponse.request_id, eventResponse.error_code);
        }
        if (!isSuccessCode(reportResponse.error_code)) {
            return handleApiError(reportResponse.request_id, reportResponse.error_code);
        }

        const eventSource = Array.isArray(eventResponse.data) ? eventResponse.data[0] : eventResponse.data;
        const parsedEvent = toEventItem((eventSource ?? {}) as Record<string, unknown>);
        setEventData(parsedEvent);

        const mappedReports = (Array.isArray(reportResponse.data) ? reportResponse.data : [])
            .map((item: Record<string, unknown>) => toReportItem(item))
            .filter((report) => report.id > 0 && report.event_id === parsedEvent.id);
        setReports(mappedReports);

        if (isAthleteView) {
            setAthletes([]);
            return;
        }

        const membersResponse = await members(association.id, 0, 100);
        if (!isSuccessCode(membersResponse.error_code)) {
            return handleApiError(membersResponse.request_id, membersResponse.error_code);
        }

        const mappedMembers = (Array.isArray(membersResponse.data) ? membersResponse.data : [])
            .map((item: Record<string, unknown>) => ({
                id: Number(item.id ?? 0),
                name: String(item.name ?? ""),
                surname: String(item.surname ?? ""),
                role: getRole(item).toLowerCase(),
            }))
            .filter((member) => member.id > 0);
        setAthletes(mappedMembers.filter((member) => member.role === "athlete"));
    }, [addNotification, association.id, associationId, eventId, handleApiError, isAthleteView, nav]);

    useEffect(() => {
        setLoadingStatus(true);
        loadData().finally(() => setLoadingStatus(false));
    }, [loadData, setLoadingStatus]);

    const eventDays = useMemo(() => {
        if (!eventData) return [];
        return buildDateRange(eventData.start_date, eventData.end_date);
    }, [eventData]);

    const viewButtons: TableButtons[] = [
        {
            button: <FontAwesomeIcon icon={faNotesMedical} title="View report" />,
            bc: "green",
            placeholder: "Compile",
            buttonByRow: (row) => {
                if (row.status === "Draft" || row.status === "Submitted") {
                    return <FontAwesomeIcon icon={faEye} title="View report" />;
                }
                if (row.status === "Not compiled" && isCoachOrPresident) {
                    return <FontAwesomeIcon icon={faBell} title="Send reminder" />;
                }
                return <FontAwesomeIcon icon={faNotesMedical} title="Compile" />;
            },
            action: {
                param: "nav_payload",
                function: (payload: { report_id: number; status: string; day: string; athlete_user_id?: number }) => {
                    if (payload.status === "Not compiled" && isCoachOrPresident) {
                        sendReminderForMissingReport({ day: payload.day, athlete_user_id: payload.athlete_user_id });
                        return;
                    }

                    if (!payload.report_id) {
                        if (isCoachOrPresident) {
                            return;
                        }
                        nav(`/report/create?event_id=${eventData?.id ?? 0}&day=${payload.day}`);
                        return;
                    }

                    nav(`/report/view?report_id=${payload.report_id}`);
                }
            }
        }
    ];

    const dayColumns: Column[] = [
        { header: "Day", accessor: "day" },
        { header: "Status", accessor: "status" },
    ];

    const athleteRows = useMemo(() => {
        if (!eventData) return [];
        return eventDays.map((day) => {
            const report = findMemberReportForDay(reports, eventData.id, day, user.id);
            const status = report
                ? report.status === "submitted" ? "Submitted" : "Draft"
                : day > today
                    ? "Not compiled yet"
                    : "Not compiled";
            return { day, status, report_id: report?.id ?? 0, nav_payload: { report_id: report?.id ?? 0, status, day, athlete_user_id: user.id } };
        });
    }, [eventData, eventDays, reports, today, user.id]);

    const coachGroups = useMemo(() => {
        if (!eventData) return [];
        return athletes.map((athlete) => {
            const rows = eventDays.map((day) => {
                const report = findMemberReportForDay(reports, eventData.id, day, athlete.id);
                const status = report
                    ? report.status === "submitted" ? "Submitted" : "Draft"
                    : day > today
                        ? "Not compiled yet"
                        : "Not compiled";
                return { day, status, report_id: report?.id ?? 0, nav_payload: { report_id: report?.id ?? 0, status, day, athlete_user_id: athlete.id } };
            });
            return {
                athlete,
                rows,
            };
        });
    }, [athletes, eventData, eventDays, reports, today]);

    const orphanReportRows = useMemo(() => {
        const athleteIds = new Set(athletes.map((athlete) => athlete.id));
        return reports
            .filter((report) => !athleteIds.has(resolveOwnerId(report)))
            .map((report) => ({
                day: report.event_day,
                status: report.status === "submitted" ? "Submitted" : "Draft",
                report_id: report.id,
                nav_payload: {
                    report_id: report.id,
                    status: report.status === "submitted" ? "Submitted" : "Draft",
                    day: report.event_day,
                    athlete_user_id: resolveOwnerId(report),
                },
            }));
    }, [athletes, reports]);

    if (loading) {
        return <LoadingScreenLocal />;
    }

    if (!eventData) {
        return null;
    }

    return (
        <div className="report-event-overview-screen">
            <h2>{eventData.name}</h2>
            <p>{eventData.start_date} - {eventData.end_date}</p>

            {isAthleteView ? (
                <TableComponent data={athleteRows} columns={dayColumns} buttons={viewButtons} />
            ) : (
                <>
                    {coachGroups.map((group) => (
                        <div key={group.athlete.id} className="report-athlete-block">
                            <h3>{group.athlete.name} {group.athlete.surname}</h3>
                            <TableComponent data={group.rows} columns={dayColumns} buttons={viewButtons} />
                        </div>
                    ))}
                    {orphanReportRows.length > 0 && (
                        <div className="report-athlete-block">
                            <h3>Other compiled reports</h3>
                            <TableComponent data={orphanReportRows} columns={dayColumns} buttons={viewButtons} />
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default ReportEventOverviewScreen;
